import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';

import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import API_URL from '../../config/api';

import './ReviewImport.css';

const REQUIRED_COLUMNS = ['DESCRICAO', 'LOTE', 'BLOCO', 'BE', 'OBSERVACAO'];

const removeAccents = value =>
	String(value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '');

const normalizeHeader = value =>
	removeAccents(value)
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '');

const normalizeCode = value =>
	removeAccents(value)
		.trim()
		.toUpperCase()
		.replace(/\s+/g, ' ');

const findHeader = (rows, requiredHeaders) => {
	for (let rowIndex = 0; rowIndex < Math.min(rows.length, 30); rowIndex += 1) {
		const headerMap = {};

		(rows[rowIndex] || []).forEach((value, columnIndex) => {
			const normalized = normalizeHeader(value);
			if (normalized) headerMap[normalized] = columnIndex;
		});

		if (requiredHeaders.every(header => headerMap[header] !== undefined)) {
			return { rowIndex, headerMap };
		}
	}

	return null;
};

const readWorkbook = async file => {
	const buffer = await file.arrayBuffer();
	return XLSX.read(buffer, { type: 'array' });
};

const validateWorkbook = async file => {
	const errors = [];
	const workbook = await readWorkbook(file);

	const pendingSheetName = workbook.SheetNames.find(
		name => normalizeCode(name) === 'PENDENCIAS'
	);
	const servicesSheetName = workbook.SheetNames.find(
		name => normalizeCode(name) === 'SERVICOS'
	);

	if (!pendingSheetName) {
		errors.push({ linha: null, campo: 'PENDENCIAS', mensagem: "A aba 'PENDENCIAS' não foi encontrada." });
	}

	if (!servicesSheetName) {
		errors.push({ linha: null, campo: 'SERVICOS', mensagem: "A aba 'SERVICOS' não foi encontrada." });
	}

	if (errors.length > 0) {
		return { errors, rowCount: 0, serviceCount: 0, services: [] };
	}

	const pendingRows = XLSX.utils.sheet_to_json(workbook.Sheets[pendingSheetName], {
		header: 1,
		defval: '',
		raw: false,
	});

	const serviceRows = XLSX.utils.sheet_to_json(workbook.Sheets[servicesSheetName], {
		header: 1,
		defval: '',
		raw: false,
	});

	const pendingHeader = findHeader(pendingRows, REQUIRED_COLUMNS);
	const serviceHeader = findHeader(serviceRows, ['CODIGODAPLANILHA']);

	if (!pendingHeader) {
		errors.push({
			linha: 1,
			campo: 'COLUNAS',
			mensagem: 'Não foram encontradas todas as colunas obrigatórias: DESCRICAO, LOTE, BLOCO, BE e OBSERVAÇÃO.',
		});
	}

	if (!serviceHeader) {
		errors.push({
			linha: null,
			campo: 'SERVICOS',
			mensagem: "A coluna 'CÓDIGO DA PLANILHA' não foi encontrada na aba SERVICOS.",
		});
	}

	if (errors.length > 0) {
		return { errors, rowCount: 0, serviceCount: 0, services: [] };
	}

	const serviceColumn = serviceHeader.headerMap.CODIGODAPLANILHA;
	const allowedServices = new Set();

	for (let i = serviceHeader.rowIndex + 1; i < serviceRows.length; i += 1) {
		const code = normalizeCode(serviceRows[i]?.[serviceColumn]);
		if (code) allowedServices.add(code);
	}

	if (allowedServices.size === 0) {
		errors.push({
			linha: null,
			campo: 'SERVICOS',
			mensagem: 'Nenhum código de serviço foi encontrado na aba SERVICOS.',
		});
	}

	let rowCount = 0;
	const servicesUsed = new Set();
	const columns = pendingHeader.headerMap;

	for (let i = pendingHeader.rowIndex + 1; i < pendingRows.length; i += 1) {
		const row = pendingRows[i] || [];
		const values = {
			descricao: String(row[columns.DESCRICAO] ?? '').trim(),
			lote: String(row[columns.LOTE] ?? '').trim(),
			bloco: String(row[columns.BLOCO] ?? '').trim(),
			be: String(row[columns.BE] ?? '').trim(),
			observacao: String(row[columns.OBSERVACAO] ?? '').trim(),
		};

		const isEmpty = Object.values(values).every(value => !value);
		if (isEmpty) continue;

		rowCount += 1;
		const excelRow = i + 1;
		const code = normalizeCode(values.descricao);

		if (!code) {
			errors.push({ linha: excelRow, campo: 'DESCRICAO', mensagem: 'DESCRICAO é obrigatória.' });
		} else if (!allowedServices.has(code)) {
			errors.push({
				linha: excelRow,
				campo: 'DESCRICAO',
				mensagem: `O valor '${values.descricao}' não existe na aba SERVICOS.`,
			});
		} else {
			servicesUsed.add(code);
		}

		const lote = Number(values.lote);
		if (!Number.isInteger(lote) || lote < 1 || lote > 12) {
			errors.push({ linha: excelRow, campo: 'LOTE', mensagem: 'LOTE deve ser um número inteiro entre 1 e 12.' });
		}

		if (!values.bloco) {
			errors.push({ linha: excelRow, campo: 'BLOCO', mensagem: 'BLOCO é obrigatório.' });
		}

		if (!values.be) {
			errors.push({ linha: excelRow, campo: 'BE', mensagem: 'BE é obrigatório.' });
		} else if (!/^BE\d{2}$/i.test(values.be)) {
			errors.push({ linha: excelRow, campo: 'BE', mensagem: `O valor '${values.be}' deve seguir o formato BE##.` });
		}

		if (!values.observacao) {
			errors.push({ linha: excelRow, campo: 'OBSERVACAO', mensagem: 'OBSERVAÇÃO é obrigatória.' });
		}
	}

	if (rowCount === 0) {
		errors.push({ linha: null, campo: 'PENDENCIAS', mensagem: 'Nenhuma linha preenchida foi encontrada.' });
	}

	return {
		errors,
		rowCount,
		serviceCount: servicesUsed.size,
		services: [...servicesUsed].sort(),
	};
};

export default function ReviewImport() {
	const [file, setFile] = useState(null);
	const [validating, setValidating] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [validation, setValidation] = useState(null);
	const [result, setResult] = useState(null);

	const canUpload = useMemo(
		() => Boolean(file && validation && validation.errors.length === 0 && !validating && !uploading),
		[file, validation, validating, uploading]
	);

	const handleFileChange = async event => {
		const selected = event.target.files?.[0] || null;
		setFile(selected);
		setValidation(null);
		setResult(null);

		if (!selected) return;

		if (!selected.name.toLowerCase().endsWith('.xlsx')) {
			setValidation({
				errors: [{ linha: null, campo: 'ARQUIVO', mensagem: 'Selecione um arquivo .xlsx.' }],
				rowCount: 0,
				serviceCount: 0,
				services: [],
			});
			return;
		}

		try {
			setValidating(true);
			const analysis = await validateWorkbook(selected);
			setValidation(analysis);

			if (analysis.errors.length === 0) {
				toast.success('Planilha validada. Pronta para importação.');
			}
		} catch (error) {
			console.error(error);
			setValidation({
				errors: [{ linha: null, campo: 'ARQUIVO', mensagem: 'Não foi possível ler a planilha.' }],
				rowCount: 0,
				serviceCount: 0,
				services: [],
			});
		} finally {
			setValidating(false);
		}
	};

	const handleUpload = async () => {
		if (!canUpload) return;

		const token = JSON.parse(atob(localStorage.getItem('token') || '""'));
		const formData = new FormData();
		formData.append('arquivoExcel', file);

		try {
			setUploading(true);
			setResult(null);

			const response = await fetch(`${API_URL}/api/revisao-produto/importar`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				body: formData,
			});

			const data = await response.json().catch(() => null);

			if (!response.ok) {
				if (data?.erros) {
					setValidation(prev => ({
						...(prev || { rowCount: 0, serviceCount: 0, services: [] }),
						errors: data.erros,
					}));
				}

				throw new Error(data?.mensagem || data?.message || 'Falha ao importar planilha.');
			}

			setResult(data);
			toast.success('Pendências importadas com sucesso.');
		} catch (error) {
			toast.error(error.message || 'Erro ao importar planilha.');
		} finally {
			setUploading(false);
		}
	};

	return (
		<div className="review-import-page">
			<Navbar />
			<ToastContainer position="top-right" autoClose={3500} />

			<main className="review-import-content">
				<section className="review-import-card">
					<div className="review-import-heading">
						<div>
							<h1>Importar Pendências de Revisão</h1>
							<p>
								Envie a planilha padronizada contendo as abas <strong>PENDENCIAS</strong> e <strong>SERVICOS</strong>.
							</p>
						</div>
					</div>

					<div className="review-import-upload">
						<label htmlFor="review-file">Planilha (.xlsx)</label>
						<input
							id="review-file"
							type="file"
							accept=".xlsx"
							onChange={handleFileChange}
						/>
						<button type="button" disabled={!canUpload} onClick={handleUpload}>
							{uploading ? 'Importando...' : 'Importar para o banco'}
						</button>
					</div>

					{validating && (
						<div className="review-import-info">Validando estrutura e valores da planilha...</div>
					)}

					{validation && !validating && (
						<section className={`review-validation ${validation.errors.length ? 'has-errors' : 'is-valid'}`}>
							<h2>{validation.errors.length ? 'Validação encontrou problemas' : 'Planilha válida'}</h2>

							<div className="review-validation-summary">
								<div><strong>{validation.rowCount}</strong><span>linhas de pendência</span></div>
								<div><strong>{validation.serviceCount}</strong><span>códigos utilizados</span></div>
								<div><strong>{validation.errors.length}</strong><span>erros</span></div>
							</div>

							{validation.services.length > 0 && (
								<div className="review-service-tags">
									{validation.services.map(service => <span key={service}>{service}</span>)}
								</div>
							)}

							{validation.errors.length > 0 && (
								<div className="review-error-table-wrap">
									<table className="review-error-table">
										<thead>
											<tr><th>Linha</th><th>Campo</th><th>Problema</th></tr>
										</thead>
										<tbody>
											{validation.errors.map((error, index) => (
												<tr key={`${error.linha ?? 'x'}-${error.campo}-${index}`}>
													<td>{error.linha ?? '-'}</td>
													<td>{error.campo}</td>
													<td>{error.mensagem}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</section>
					)}

					{result && (
						<section className="review-import-result">
							<h2>Resultado da importação</h2>
							<div className="review-result-grid">
								<div><strong>{result.totalLinhas}</strong><span>linhas lidas</span></div>
								<div><strong>{result.totalGrupos}</strong><span>PINs/grupos</span></div>
								<div><strong>{result.inseridos}</strong><span>novos</span></div>
								<div><strong>{result.atualizados}</strong><span>atualizados</span></div>
								<div><strong>{result.semAlteracao}</strong><span>sem alteração</span></div>
								<div><strong>{result.reabertos}</strong><span>reabertos</span></div>
							</div>
						</section>
					)}
				</section>
			</main>

			<Footer />
		</div>
	);
}
